import {
  CustomRange,
  CustomRangeValidation,
} from "@portal/BaseComponents/Filters/type";
import { Form, useForm } from "@portal/BaseComponents/Form/AntForm";
import { StyledFormItem } from "@portal/BaseComponents/Form/style";
import { Col, Row } from "@portal/BaseComponents/Grid/AntGrid";
import { InputNumber } from "@portal/BaseComponents/Input/AntInput";
import { OnInputNumberChange } from "@portal/BaseComponents/Input/type";
import { TypographyText } from "@portal/BaseComponents/Typography/AntTypography";
import { CustomFiledBetweenOperands } from "@portal/types/entities/custom-field";
import { useState } from "react";
import { customFieldRange, useNumericRangeValidation } from "../hooks";

export type OnCustomRangeChange = [
  value: CustomFiledBetweenOperands["value"],
  validationStatus: CustomRangeValidation["validateStatus"]
];
interface NumericCustomRangeProps {
  value: CustomFiledBetweenOperands["value"];
  allowDecimal: boolean;
  onChange: (...args: OnCustomRangeChange) => void;
}

function NumericCustomRange(props: NumericCustomRangeProps) {
  const { allowDecimal, value: defaultValue, onChange } = props;
  const [range, setRange] = useState<CustomRange<typeof customFieldRange>>({
    from: { value: Number(defaultValue.from) },
    to: { value: Number(defaultValue.to) },
  });
  const [form] = useForm();
  const { formValidation, toValidation } = useNumericRangeValidation({
    allowDecimal,
  });
  function handleFromChange(...args: OnInputNumberChange) {
    const [value] = args;
    const values = value !== null ? Number(value) : null;
    const validationStatus = formValidation(values, range);
    setRange((previousValue) => {
      return {
        from: { ...validationStatus, value: Number(value) },
        to:
          validationStatus.validateStatus === "success"
            ? {
                ...previousValue.to,
                errorMsg: null,
                validateStatus: "success",
              }
            : { ...previousValue.to },
      };
    });
    onChange(
      { from: String(value), to: String(range.to.value) },
      validationStatus.validateStatus
    );
  }
  function handleToChange(...args: OnInputNumberChange) {
    const [value] = args;
    const values = value || value === 0 ? Number(value) : null;
    const validationStatus = toValidation(values, range);
    setRange((previousValue) => {
      return {
        from:
          validationStatus.validateStatus === "success"
            ? {
                ...previousValue.from,
                errorMsg: null,
                validateStatus: "success",
              }
            : { ...previousValue.from },
        to: { ...validationStatus, value: Number(value) },
      };
    });
    onChange(
      { from: String(range.from.value), to: String(value) },
      validationStatus.validateStatus
    );
  }

  const FromItem = (
    <StyledFormItem
      shouldUpdate
      validateStatus={range.from.validateStatus}
      help={range.from.errorMsg}
      className="from-value"
      initialValue={range.from.value}
      name="from"
      style={{ width: "90px" }}
      fieldId="From"
    >
      <InputNumber
        onChange={handleFromChange}
        controls={false}
        placeholder="From"
      />
    </StyledFormItem>
  );
  const ToItem = (
    <StyledFormItem
      shouldUpdate
      validateStatus={range.to.validateStatus}
      help={range.to.errorMsg}
      className="to-value"
      initialValue={range.to.value}
      name="to"
      fieldId="To"
      style={{ width: "90px" }}
    >
      <InputNumber
        onChange={handleToChange}
        placeholder="To"
        controls={false}
      />
    </StyledFormItem>
  );

  const Text = <TypographyText>and</TypographyText>;

  const isError =
    range.from.validateStatus === "error" ||
    range.to.validateStatus === "error";
  return (
    <Form form={form}>
      <Row gutter={[8, 8]} align={isError ? "top" : "middle"}>
        <Col>{FromItem}</Col>
        <Col>{Text}</Col>
        <Col>{ToItem}</Col>
      </Row>
    </Form>
  );
}

export { NumericCustomRange };
